import { defineComponent as _defineComponent } from 'vue';
import { BOverlay, BFormGroup, BFormInput, BButton, BCard, BAlert } from 'bootstrap-vue';
import FrameVisualisation from 'public/components/picture-frames/FrameVisualisation.vue';
import StandardSizeSelector from 'public/components/StandardSizeSelector.vue';
import MountboardColours from 'public/components/MountboardColours.vue';
import GlazingOptions from 'public/components/picture-frames/Value/GlazingOptions.vue';
import MouldingOptions from 'public/components/picture-frames/Value/MouldingOptions.vue';
import FrameDesignSelector from 'public/components/picture-frames/Value/FrameDesignSelector.vue';
import DiscountsBreakdown from 'public/components/DiscountsBreakdown.vue';
import FrameSpecification from 'public/components/picture-frames/Value/FrameSpecification.vue';
import FixingOptions from 'public/components/picture-frames/Value/FixingOptions.vue';
import debounce from 'lodash/debounce';
import { computed, onMounted, ref, watch } from 'vue';
import { addToCart as addToCartApi } from 'public/modules/cart';
import { checkFrameStock, checkMountStock } from 'public/modules/stock';
import { getVisualisationUrl } from 'public/modules/frameDesigner';
import { getVatMode } from 'public/modules/helpers';
import { validateFrame, validateMount, validateSheet } from 'public/modules/validation';
import { frameDelivery, sheetDelivery } from 'public/modules/delivery';
import { useAxios } from 'composables/useAxios';
import { useGtag } from 'composables/useGtag';
export default /*#__PURE__*/ _defineComponent({
    __name: 'value',
    props: {
        groupId: null
    },
    setup(__props) {
        const props = __props;
        const vatMode = ref('ex');
        const loading = ref(true);
        const loadingPrice = ref(true);
        const loadingDiscount = ref(true);
        const loadingStock = ref(true);
        const quantity = ref(1);
        const length = ref(null);
        const glazing = ref(null);
        const fixings = ref('hanger');
        const group = ref(null);
        const selectedBoard = ref({
            category: { id: 10, name: 'Whitecore' },
            category_id: 10,
            description: 'The brightest white available in our catalogue.  Whilst not as cold-white as printer paper or cheap mass-produced mountboard, it is a crisp white board with white bevel that suits art paper and high quality photographic prints.',
            dominant_colour: '#fff',
            id: 102,
            name: 'Snow White (8001) ',
            sheet_height: 1120,
            sheet_width: 1630,
            slug: 'snow-white-8001',
            stock_lookup_sku: '8001',
            thickness: 1.4
        });
        const itemSize = ref({ width: 210, length: 297, name: 'A4', type: 'a' });
        const frameSize = ref({ width: 210, length: 297, name: 'A4', type: 'a' });
        const discountedPrice = ref('0.00');
        const discount = ref(0);
        const price = ref('0.00');
        const exVatPrice = ref('0.00');
        const stock = ref({ frame: false, mount: false });
        const validation = ref({
            frame: { isValid: true, problems: [], warnings: [] },
            mount: { isValid: true, problems: [], warnings: [] },
            glazing: { isValid: true, problems: [], warnings: [] }
        });
        const deliveryValidation = ref({
            frame: { isValid: true, problems: [], warnings: [] },
            glazing: { isValid: true, problems: [], warnings: [] }
        });
        const isCollectionOnly = computed(() => !deliveryValidation.value.frame.isValid || !deliveryValidation.value.glazing.isValid);
        const isBuyButtonValid = computed(() => stock.value.frame
            && stock.value.mount
            && validation.value.frame.isValid
            && validation.value.mount.isValid
            && validation.value.glazing.isValid);
        const isBuyButtonLoading = computed(() => loading.value || loadingPrice.value || loadingDiscount.value || loadingStock.value);
        const isBuyButtonDisabled = computed(() => !isBuyButtonValid.value || isBuyButtonLoading.value);
        const totalPrice = computed(() => (parseFloat(discountedPrice.value.replace(/,/g, '')) * quantity.value).toFixed(2));
        const axios = useAxios();
        const onQuantityChange = async () => {
            loadingDiscount.value = true;
            if (quantity.value == 1) {
                discountedPrice.value = price.value;
                discount.value = 0;
            }
            else {
                const { data } = await axios.get('pricing/discounts/discountedPrice', {
                    params: {
                        sku: `UVF-${length.value?.sku}`,
                        price: exVatPrice.value.replace(/,/g, ''),
                        quantity: quantity.value || 1
                    }
                });
                discountedPrice.value = data.price;
                discount.value = data.discount;
            }
            await checkStock();
            loadingDiscount.value = false;
        };
        const debouncedQuantityChange = debounce(onQuantityChange, 350);
        watch(quantity, () => debouncedQuantityChange());
        const artworkSize = computed(() => ({
            width: Math.min(itemSize.value.width, itemSize.value.length),
            length: Math.max(itemSize.value.width, itemSize.value.length),
            name: itemSize.value.name
        }));
        const rebateSize = computed(() => ({
            width: Math.min(frameSize.value.width, frameSize.value.length) + 3,
            length: Math.max(frameSize.value.width, frameSize.value.length) + 3
        }));
        const formattedItemSize = computed(() => ({
            width: Math.min(itemSize.value.width, itemSize.value.length),
            length: Math.max(itemSize.value.width, itemSize.value.length)
        }));
        const apertureSize = computed(() => ({
            width: formattedItemSize.value.width - 6,
            length: formattedItemSize.value.length - 6
        }));
        const hasMount = computed(() => itemSize.value.name !== frameSize.value.name);
        const frameDescription = computed(() => hasMount.value
            ? `${frameSize.value.name} to fit ${itemSize.value.name}`
            : `${itemSize.value.name} (No Mount)`);
        const mountOption = computed(() => {
            if (hasMount.value) {
                const yBorder = ((rebateSize.value.length - apertureSize.value.length) - 3) / 2;
                const xBorder = ((rebateSize.value.width - apertureSize.value.width) - 3) / 2;
                return {
                    type: 'single',
                    top: selectedBoard.value,
                    bottom: null,
                    reveal: 0,
                    borders: { top: yBorder, bottom: yBorder, left: xBorder, right: xBorder }
                };
            }
            return { type: 'none', top: null, bottom: null, reveal: 0, borders: { top: 0, bottom: 0, left: 0, right: 0 } };
        });
        const updateDelivery = async () => {
            const promises = [];
            if (length.value) {
                promises.push(frameDelivery(length.value.sku, rebateSize.value).then(v => (deliveryValidation.value.frame = v)));
            }
            else {
                deliveryValidation.value.frame = { isValid: true, problems: [], warnings: [] };
            }
            if (glazing.value) {
                promises.push(sheetDelivery(`${glazing.value.category?.name == 'Acrylic Glass' ? 'A' : ''}G-${glazing.value.id}`, { width: frameSize.value.width, length: frameSize.value.length }).then(v => (deliveryValidation.value.glazing = v)));
            }
            else {
                deliveryValidation.value.glazing = { isValid: true, problems: [], warnings: [] };
            }
            return Promise.all(promises);
        };
        const updateValidation = async () => {
            const promises = [];
            if (length.value) {
                promises.push(validateFrame(length.value.sku, rebateSize.value).then(v => (validation.value.frame = v)));
            }
            if (hasMount.value) {
                promises.push(validateMount(`MCTS-${selectedBoard.value.stock_lookup_sku}`, { width: frameSize.value.width, length: frameSize.value.length }, mountOption.value.borders).then(v => (validation.value.mount = v)));
            }
            if (glazing.value) {
                promises.push(validateSheet(`G-${glazing.value.id}`, { width: frameSize.value.width, length: frameSize.value.length }).then(v => (validation.value.glazing = v)));
            }
            return Promise.all(promises);
        };
        const updatePrice = async () => {
            if (!glazing.value)
                return;
            const { data } = await axios.get('valueFrames/price', {
                params: {
                    groupID: props.groupId,
                    lengthSku: length.value?.sku,
                    rebateWidth: rebateSize.value.width,
                    rebateLength: rebateSize.value.length,
                    glazingID: glazing.value?.id,
                    fixings: fixings.value,
                    mountSku: hasMount.value ? selectedBoard.value.stock_lookup_sku : null
                }
            });
            price.value = data.price;
            exVatPrice.value = data.exVatPrice;
            await onQuantityChange();
        };
        const update = async () => {
            loadingPrice.value = true;
            await Promise.all([updatePrice(), updateValidation(), updateDelivery()]);
            loadingPrice.value = false;
        };
        const debouncedUpdate = debounce(update, 350);
        watch([length, rebateSize, glazing, selectedBoard, fixings], () => debouncedUpdate(), { deep: true });
        const checkStock = async () => {
            loadingStock.value = true;
            if (length.value) {
                stock.value.frame = await checkFrameStock(`BF-${length.value.sku}`, rebateSize.value, quantity.value);
            }
            else {
                stock.value.frame = true;
            }
            if (hasMount.value) {
                stock.value.mount = await checkMountStock(`MCTS-${selectedBoard.value.stock_lookup_sku}`, rebateSize.value, quantity.value);
            }
            else {
                stock.value.mount = true;
            }
            loadingStock.value = false;
        };
        const { event } = useGtag();
        const onAddToCart = () => {
            const item = {
                sku: `UVF-${length.value?.sku}`,
                qty: quantity.value,
                attributes: {
                    'To Fit': `${formattedItemSize.value.width}x${formattedItemSize.value.length}mm ${itemSize.value.name ? `(${itemSize.value.name})` : ''}`.trim(),
                    'Internal Frame Size - Rebate Size': `${rebateSize.value.width}x${rebateSize.value.length}mm`,
                    'Glass': glazing.value?.name || '2mm Float Glass',
                    'Backing': `2.3mm Craftboard (${frameSize.value.width}x${frameSize.value.length}mm)`,
                    'Fixings': fixings.value == 'hanger' ? 'Easy Hanger' : 'Picture Framing Cord and 2 D-Rings Screws',
                    'Delivery Available': isCollectionOnly.value ? 'false' : 'true',
                    'Image': getVisualisationUrl(length.value?.sku || '', rebateSize.value, apertureSize.value, mountOption.value, true)
                },
                price: exVatPrice.value
            };
            if (hasMount.value) {
                item.attributes['Mount'] = `Single ${mountOption.value.borders.top}x${mountOption.value.borders.left}x${mountOption.value.borders.right}x${mountOption.value.borders.bottom}mm mount (Aperture ${apertureSize.value.width}x${apertureSize.value.length}mm)`;
                item.attributes['Mount Colour'] = selectedBoard.value.name;
            }
            event('add_to_cart', {
                items: [{ id: item.sku, name: `${item.sku} - Value Frame`, brand: 'Brampton Framing', category: 'Ultra Value Frames', quantity: quantity.value, price: exVatPrice.value }]
            });
            addToCartApi(item, false);
        };
        onMounted(async () => {
            loading.value = true;
            const { data } = await axios.get('valueFrames/group', { params: { id: props.groupId } });
            group.value = data;
            length.value = group.value.lengths[0];
            vatMode.value = getVatMode();
            await update();
            loading.value = false;
        });
        return { __sfc: true, props, vatMode, loading, loadingPrice, loadingDiscount, loadingStock, quantity, length, glazing, fixings, group, selectedBoard, itemSize, frameSize, discountedPrice, discount, price, exVatPrice, stock, validation, deliveryValidation, isCollectionOnly, isBuyButtonValid, isBuyButtonLoading, isBuyButtonDisabled, totalPrice, axios, onQuantityChange, debouncedQuantityChange, artworkSize, rebateSize, formattedItemSize, apertureSize, hasMount, frameDescription, mountOption, updateDelivery, updateValidation, updatePrice, update, debouncedUpdate, checkStock, event, onAddToCart, BOverlay, BFormGroup, BFormInput, BButton, BCard, BAlert, FrameVisualisation, StandardSizeSelector, MountboardColours, GlazingOptions, MouldingOptions, FrameDesignSelector, DiscountsBreakdown, FrameSpecification, FixingOptions, debounce, computed, onMounted, ref, watch, addToCartApi, checkFrameStock, checkMountStock, getVisualisationUrl, getVatMode, validateFrame, validateMount, validateSheet, frameDelivery, sheetDelivery, useAxios, useGtag };
    }
});
